// client/src/api/ApiLeagues.js
// Import the flex module for making HTTP requests
import flex from './FlexFetch'
import {
  requireStrictResponseEnvelope,
  unwrapListPayload,
  unwrapStrictPayload,
} from './listPayloadUtils'

// Base endpoint for league-related API requests
const league_endpoint = '/api/1.0/leagues/'

// Helper function to standardize error handling
const handleApiError = (error, defaultMessage) => {
  const rawError = error.response?.data?.error
  const errorMessage =
    (typeof rawError === 'string' ? rawError : rawError?.message) ||
    error.message ||
    defaultMessage
  throw new Error(errorMessage)
}

// Fetches all league data
async function getAll() {
  try {
    const cacheBust = `all?ts=${Date.now()}`
    const response = await flex.flexFetch(league_endpoint + cacheBust, {
      cache: 'no-store',
    })
    // Processes the response and returns the result
    const result = await flex.handleResponse(response)
    return unwrapListPayload(result)
  } catch (error) {
    handleApiError(error, 'Failed to fetch leagues')
  }
}

// Creates a new league entry with the provided league data
async function create(league) {
  try {
    const response = await flex.flexFetch(league_endpoint + 'post', {
      method: 'POST',
      body: JSON.stringify(league), // Converts the league data to a JSON string
    })
    // Returns the response after processing
    const result = await flex.handleResponse(response)
    return unwrapStrictPayload(result)
  } catch (error) {
    handleApiError(error, 'Failed to create league')
  }
}

// Deletes a league entry based on the provided league ID
async function remove(league_id) {
  try {
    const response = await flex.flexFetch(
      league_endpoint + `delete?league_id=${league_id}`,
      { method: 'DELETE' }, // Specifies the DELETE method for the request
    )
    // Processes and returns the response
    const result = await flex.handleResponse(response)
    return unwrapStrictPayload(result)
  } catch (error) {
    handleApiError(error, 'Failed to delete league')
  }
}

// Updates a league's state
async function updateState(league_id, state) {
  try {
    const response = await flex.flexFetch(league_endpoint + 'updateState', {
      method: 'PATCH',
      body: JSON.stringify({ league_id, state }),
    })
    const result = await flex.handleResponse(response)
    return unwrapStrictPayload(result)
  } catch (error) {
    handleApiError(error, 'Failed to update league state')
  }
}

// Publishes a league's divisions and updates its status to pending_start
async function publishLeague(league_id) {
  return await updateState(league_id, 'pending_start')
}

// Fetches leagues that the current user is registered for
async function getMyLeagues() {
  try {
    const response = await flex.flexFetch(league_endpoint + 'myLeagues')
    // Processes and returns the response
    const result = await flex.handleResponse(response)
    return unwrapListPayload(result)
  } catch (error) {
    handleApiError(error, 'Failed to fetch your leagues')
  }
}

// Fetches a specific league by ID
async function getLeague(leagueId) {
  try {
    const response = await flex.flexFetch(
      league_endpoint + `get?league_id=${leagueId}`,
    )
    const result = await flex.handleResponse(response)
    return unwrapStrictPayload(result)
  } catch (error) {
    handleApiError(error, `Failed to fetch league ${leagueId}`)
  }
}

async function getLeagueLevels(leagueId) {
  try {
    const response = await flex.flexFetch(
      league_endpoint + `levelSettings?league_id=${leagueId}`,
    )
    const result = await flex.handleResponse(response)
    return unwrapStrictPayload(result)
  } catch (error) {
    handleApiError(
      error,
      `Failed to fetch league levels for league ${leagueId}`,
    )
  }
}

async function addLeagueLevels(league_id, skill_levels) {
  try {
    const response = await flex.flexFetch(league_endpoint + 'addLevels', {
      method: 'POST',
      body: JSON.stringify({ league_id, skill_levels }),
    })
    const result = await flex.handleResponse(response)
    return unwrapStrictPayload(result)
  } catch (error) {
    handleApiError(error, 'Failed to add league levels')
  }
}

async function deactivateLeagueLevels(league_id, skill_levels, reason = '') {
  try {
    const response = await flex.flexFetch(
      league_endpoint + 'deactivateLevels',
      {
        method: 'POST',
        body: JSON.stringify({ league_id, skill_levels, reason }),
      },
    )
    const result = await flex.handleResponse(response)
    return unwrapStrictPayload(result)
  } catch (error) {
    handleApiError(error, 'Failed to deactivate league levels')
  }
}

// Updates an existing league's details
async function updateLeague(leagueId, league) {
  try {
    const response = await flex.flexFetch(league_endpoint + 'update', {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ ...league, id: leagueId }),
    })
    const result = await flex.handleResponse(response)
    return unwrapStrictPayload(result)
  } catch (error) {
    handleApiError(error, 'Failed to update league')
  }
}

async function extendLeague(leagueId, extensionDays) {
  try {
    const response = await flex.flexFetch(league_endpoint + 'extend', {
      method: 'PATCH',
      body: JSON.stringify({
        league_id: leagueId,
        extension_days: extensionDays,
      }),
    })

    const result = await flex.handleResponse(response)
    return unwrapStrictPayload(result)
  } catch (error) {
    handleApiError(error, 'Failed to extend league')
  }
}

// Fetches unplayed opponents for the current user in a specific league
async function getUnplayedOpponents(leagueId) {
  try {
    const response = await flex.flexFetch(
      league_endpoint + `unplayedOpponents?league_id=${leagueId}`,
    )
    const result = await flex.handleResponse(response)
    return unwrapStrictPayload(result)
  } catch (error) {
    handleApiError(
      error,
      `Failed to fetch unplayed opponents for league ${leagueId}`,
    )
  }
}

// Set home court for a player in a league
async function setHomeCourt({ league_id, player_id, court_id }) {
  try {
    const response = await flex.flexFetch(league_endpoint + 'setHomeCourt', {
      method: 'PATCH',
      body: JSON.stringify({ league_id, player_id, court_id }),
    })
    const result = await flex.handleResponse(response)
    return unwrapStrictPayload(result)
  } catch (error) {
    handleApiError(error, 'Failed to set home court')
  }
}

// Fetches league-wide standings data including all players, matches, and divisions
async function getLeagueStandings(leagueId, leagueLevelId = null) {
  try {
    const qs = leagueLevelId
      ? `standings?league_id=${leagueId}&league_level_id=${leagueLevelId}`
      : `standings?league_id=${leagueId}`
    const response = await flex.flexFetch(league_endpoint + qs)
    const result = await flex.handleResponse(response)
    return requireStrictResponseEnvelope(result)
  } catch (error) {
    handleApiError(error, 'Failed to fetch league standings')
  }
}

// Downloads a CSV file containing contact information for all league participants
async function downloadParticipantsCsv(leagueId) {
  try {
    const response = await flex.flexFetch(
      league_endpoint + `exportParticipants?league_id=${leagueId}`,
    )

    // Check if response is successful
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))

      // Provide more specific error messages based on status code
      if (response.status === 403) {
        throw new Error('You do not have permission to download this list')
      } else if (response.status === 404) {
        throw new Error('League not found')
      } else if (response.status === 400) {
        throw new Error(errorData.error?.message || 'Invalid request')
      }

      throw new Error(
        errorData.error?.message || 'Failed to download participant list',
      )
    }

    // Get the CSV content as text
    const csvContent = await response.text()

    // Create a blob and trigger download
    const blob = new Blob([csvContent], { type: 'text/csv' })
    const url = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url

    // Extract filename from Content-Disposition header if available
    const contentDisposition = response.headers.get('Content-Disposition')
    let filename = 'league_participants.csv'
    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename="?(.+)"?/)
      if (filenameMatch) {
        filename = filenameMatch[1]
      }
    }

    link.setAttribute('download', filename)
    document.body.appendChild(link)
    link.click()
    link.remove()
    window.URL.revokeObjectURL(url)

    // Extract participant count from custom header if available
    const participantCount = response.headers.get('X-Participant-Count')

    return {
      success: true,
      count: participantCount ? parseInt(participantCount, 10) : null,
    }
  } catch (error) {
    handleApiError(error, 'Failed to download participant list')
  }
}

// Exports the API functions for use in other modules
const ApiLeagues = {
  getAll,
  create,
  remove,
  getMyLeagues,
  publishLeague,
  updateState,
  getLeague,
  getLeagueLevels,
  addLeagueLevels,
  deactivateLeagueLevels,
  updateLeague,
  extendLeague,
  getUnplayedOpponents,
  setHomeCourt,
  getLeagueStandings,
  downloadParticipantsCsv,
}

export default ApiLeagues
